/**
 * Right-side document viewer pane used in staff case review.
 *
 * Fetches the presigned download URL for the selected document and
 * renders it inline. PDFs go in an `<iframe>`; images in `<img>`;
 * everything else falls back to a download link.
 *
 * The pane is purely visual — it does NOT own the document review
 * action (that lives in FieldReviewSection's row). When the agent
 * clicks a different document, the parent updates `selectedFile`
 * and this component refetches. Closing the pane is the parent's
 * job too (this component just exposes an `onClose` button).
 */
import { useEffect, useMemo, useState } from 'react';

import workflowService from '../../services/workflow.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { useAuthStore } from '../../stores/authStore';
import type { ReviewableFile } from './FieldReviewSection';

/** Tiled diagonal watermark identifying the staff member viewing a client
 *  document. A deterrent + traceability control: any screenshot or screen-grab
 *  of restricted client PII carries the viewer's identity and a CONFIDENTIAL
 *  mark (CNBV / SEC confidential-data handling). Applied to every staff role. */
function useWatermarkBackground(viewerTag: string): { backgroundImage: string; backgroundRepeat: 'repeat' } {
  return useMemo(() => {
    const date = new Date().toISOString().slice(0, 10);
    const text = `${viewerTag} · CONFIDENTIAL · ${date}`.replace(/[<>&"']/g, '');
    const svg =
      `<svg xmlns='http://www.w3.org/2000/svg' width='360' height='200'>` +
      `<text x='10' y='120' transform='rotate(-28 180 100)' ` +
      `fill='rgba(15,23,42,0.13)' font-family='sans-serif' font-size='14' font-weight='600'>` +
      `${text}</text></svg>`;
    return {
      backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
      backgroundRepeat: 'repeat',
    };
  }, [viewerTag]);
}


interface Props {
  caseId: string;
  file: ReviewableFile;
  onClose: () => void;
}

export default function DocumentViewerPane({
  caseId,
  file,
  onClose,
}: Readonly<Props>): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { user } = useAuthStore();
  const watermarkBg = useWatermarkBackground(user?.email ?? 'staff');

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setLoadError(null);
    (async () => {
      try {
        const u = await workflowService.getDocumentDownloadUrl(caseId, file.documentId);
        if (!cancelled) setUrl(u);
      } catch (err) {
        if (!cancelled) {
          setLoadError(getApiErrorMessage(err, 'Could not fetch the document.'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, file.documentId]);

  const isPdf = (file.mimeType ?? '').toLowerCase().includes('pdf');
  const isImage = (file.mimeType ?? '').toLowerCase().startsWith('image/');

  return (
    <aside
      className="rounded-2xl overflow-hidden flex flex-col"
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        height: 'calc(100vh - 200px)',
        position: 'sticky',
        top: 16,
      }}
    >
      <div
        className="flex items-center justify-between gap-2 px-4 py-3"
        style={{ borderBottom: `1px solid ${colors.border}`, background: colors.canvas }}
      >
        <div className="min-w-0">
          <p
            className="text-xs font-semibold uppercase tracking-wide truncate"
            style={{ color: colors.muted }}
          >
            {file.label}
          </p>
          <p className="text-[11px] font-mono truncate" style={{ color: colors.mutedSoft }}>
            {file.mimeType ?? 'unknown type'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="px-2 py-1 rounded text-xs font-medium flex-shrink-0"
          style={{
            background: colors.surface,
            color: colors.inkSoft,
            border: `1px solid ${colors.border}`,
          }}
          aria-label="Close document viewer"
        >
          ✕ Close
        </button>
      </div>

      <div className="flex-1 relative" style={{ background: colors.canvas }}>
        <div className="absolute inset-0 overflow-auto">
        {(() => {
          if (loadError) {
            return (
              <div className="p-4 text-sm" style={{ color: colors.danger }}>
                {loadError}
              </div>
            );
          }
          if (!url) {
            return (
              <div className="p-4 text-sm" style={{ color: colors.muted }}>
                Loading document…
              </div>
            );
          }
          if (isPdf) {
            return (
              <iframe
                src={url}
                title={`Document: ${file.label}`}
                className="w-full h-full block"
                style={{ minHeight: 400, border: 'none' }}
              />
            );
          }
          if (isImage) {
            return (
              <img
                src={url}
                alt={file.label}
                className="block max-w-full max-h-full mx-auto"
              />
            );
          }
          return (
            <div className="p-4 text-sm" style={{ color: colors.inkSoft }}>
              <p className="mb-2">Inline preview not available for this file type.</p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: colors.inkSoft }}
              >
                Open in new tab
              </a>
            </div>
          );
        })()}
        </div>
        {/* Confidential watermark — sits over the document viewport (fixed, not
            scrolling with the content) so it can't be scrolled out of a screen
            grab. pointer-events:none keeps the document interactive underneath. */}
        {url && !loadError && (
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{ pointerEvents: 'none', zIndex: 5, ...watermarkBg }}
          />
        )}
      </div>
    </aside>
  );
}
